import { Loader2, Plus } from "lucide-react"
import { type FormEvent, useState } from "react"
import { useComputers } from "../../computers/context"
import { readPairingInvite } from "../../computers/pairing"

/**
 * The dashed tile at the end of the computers row. It expands in place into
 * a single paste field, so adding another Mac never leaves the grid.
 */
export function AddComputerTile() {
    const computers = useComputers()
    const [open, setOpen] = useState(false)
    const [link, setLink] = useState("")
    const [error, setError] = useState<string | null>(null)

    const submit = async (event: FormEvent) => {
        event.preventDefault()
        setError(null)
        try {
            const invite = readPairingInvite(link.trim())
            if (!invite) throw new Error("That isn't a WildLark link. It starts with https://app.wildlark.ai/pair#")
            await computers.pairAndAdd(invite)
            setLink("")
            setOpen(false)
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : String(cause))
        }
    }

    if (!open) {
        return (
            <button
                type="button"
                className="btn flex min-h-[7.5rem] w-full flex-col items-center justify-center gap-2 self-start border border-dashed border-border text-sm font-medium text-muted transition-colors hover:border-primary hover:bg-primary/5 hover:text-primary"
                onClick={() => setOpen(true)}
            >
                <Plus className="h-5 w-5" />
                Add a computer
            </button>
        )
    }

    return (
        <form className="flex min-h-[7.5rem] flex-col justify-between gap-3 border border-primary/50 bg-base-200 p-4 sm:col-span-2" onSubmit={submit}>
            <div>
                <div className="text-sm font-semibold text-base-content">Add a computer</div>
                <p className="mt-1 text-xs leading-5 text-muted">
                    On the other computer, open Settings → Sharing, turn on Reachable from anywhere, then choose Add a device. Paste the link it gives you here.
                </p>
            </div>
            <div className="flex gap-2 max-[600px]:flex-col">
                <input
                    className="input h-10 min-w-0 flex-1 border border-border bg-input px-3 font-mono text-sm text-base-content outline-none focus:border-primary"
                    value={link}
                    onChange={(event) => setLink(event.target.value)}
                    placeholder="https://app.wildlark.ai/pair#…"
                    inputMode="url"
                    autoCapitalize="none"
                    autoCorrect="off"
                    autoComplete="off"
                    spellCheck={false}
                    autoFocus
                    required
                    aria-label="Pairing link"
                />
                <button
                    type="submit"
                    className="btn flex h-10 items-center justify-center gap-2 bg-primary px-4 text-sm font-medium text-primary-content disabled:opacity-50"
                    disabled={computers.busy}
                >
                    {computers.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    {computers.busy ? "Adding…" : "Add"}
                </button>
                <button
                    type="button"
                    className="btn flex h-10 items-center justify-center px-3 text-sm text-muted hover:text-base-content"
                    onClick={() => setOpen(false)}
                >
                    Cancel
                </button>
            </div>
            {error && (
                <div role="alert" className="border border-error/40 bg-error/10 px-3 py-2 text-xs text-error">
                    {error}
                </div>
            )}
        </form>
    )
}
